import React from 'react';
import { useTheme } from '@/contexts/Theme';
import { getAntdTokens } from '@/assets/theme/getThemeAntd';
import { ConfigProvider as AntdConfigProvider, theme } from 'antd';
import ptBR from 'antd/lib/locale/pt_BR';

const APP_FONT_FAMILY =
    "'Nunito Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji'";

export function ConfigProvider({ children }: { children: React.ReactNode }) {
    const { theme: themeSystem } = useTheme();
    const antdTokens = React.useMemo(
        () => getAntdTokens(themeSystem),
        [themeSystem],
    );

    const modeStyles = {
        light: {
            token: {
                colorBgBase: '#ffffff',
                colorBgContainer: '#ffffff',
                colorBgLayout: '#ffffff',
                colorBgHeader: '#020617',
                borderRadius: antdTokens.borderRadius,
                fontFamily: APP_FONT_FAMILY,
            },
            components: {
                Layout: {
                    headerBg: '#FFFFFF',
                    lightTriggerBg: '#1677ff',
                    lightTriggerColor: '#ffffff',
                },
            },
        },
        dark: {
            token: {
                borderRadius: antdTokens.borderRadius,
                fontFamily: APP_FONT_FAMILY,
            },
            components: {
                Layout: {
                    headerBg: '#141414',
                    lightTriggerBg: '#1677ff',
                },
            },
        },
    } as const;

    const antdThemeConfig = React.useMemo(
        () => ({
            token: modeStyles[themeSystem].token,
            components: {
                Layout: modeStyles[themeSystem].components.Layout,
            },
            cssVar: {
                key: themeSystem === 'dark' ? 'antd-dark' : 'antd',
            },
            algorithm:
                themeSystem === 'dark'
                    ? theme.darkAlgorithm
                    : theme.defaultAlgorithm,
        }),
        [themeSystem],
    );

    React.useEffect(() => {
        AntdConfigProvider.config({ theme: antdThemeConfig });
    }, [antdThemeConfig]);

    return (
        <AntdConfigProvider locale={ptBR} theme={antdThemeConfig}>
            {children}
        </AntdConfigProvider>
    );
}
